import React from 'react';
import ReactDOM from 'react-dom';
import { Router } from 'react-router-dom';
import { createBrowserHistory as createHistory } from 'history';
import { Provider } from 'react-redux';
import {
    createStore, combineReducers, applyMiddleware, compose,
} from 'redux';
import thunk from 'redux-thunk';
import { Grommet } from 'grommet';
import theme from 'esm-web-pro/styles/theme';
import * as Sentry from '@sentry/browser';
import 'whatwg-url';

import { IntlProvider } from 'react-intl';
import Cookie from 'js-cookie';
import bgMessages from 'locale/lang/esm-web-pro/bg';

import {
    ApiClient, WebSocketClient, JWTBackend,
} from 'speed-js-core';
import ApiClientAuthBackend from 'speed-js-core/src/websocket/auth/ApiClientAuthBackend';
import {
    APIProvider, SchemaForm, WSContext,
    reducers as speedReducers,
} from 'speed-js-react';
import { removeUser, setPermissions, setUser } from 'speed-js-react/src/redux/auth/actions';
import {
    GROMMET_UI_FALLBACK_FIELD_TYPE,
} from 'speed-js-react-web';
import { registerActionTypes } from 'speed-js-react-web/src/components/grommet2/tables/columns/Action/ActionsColumn';
import App from 'esm-web-pro/App';
import sidePanelMiddleware from 'esm-web-pro/redux/side-panel/middlewares';
import callingsMiddleware from 'esm-web-pro/redux/callings/middlewares';
import updateDocumentTitleMiddleware from 'esm-web-pro/redux/documentTitle/middlewares';
import LeavePageDialog from 'esm-web-pro/components/dialogs/LeavePageDialog';
import ACTION_TYPES from 'esm-web-pro/containers/tables/columns';
import AuthLocalStorage from 'esm-web-pro/api/auth/AuthLocalStorage';
import { BSS_URL_BASENAME_PREFIX } from 'esm-web-pro/routes/util';

import * as serviceWorker from './serviceWorker';
import { resources } from './api';
import reducers from './redux/reducers';
import { WorkflowManager, handlers } from './workflows';
import { WorkflowProvider, FORM_FIELDS } from './components';

import './styles/fonts/OpenSans/OpenSans.less';


if (process.env.NODE_ENV === 'production') {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [
            new Sentry.Integrations.GlobalHandlers({
                onerror: true,
                onunhandledrejection: false,
            }),
        ],
        ignoreErrors: ['ResizeObserver loop limit exceeded'],
    });
}


// GET LOCALE AND FETCH TRANSLATION MESSAGES
const locale = Cookie.get('locale') || 'bg';
const messages = { bg: bgMessages };

// REGISTER FORM FIELDS
SchemaForm.registerFieldTypes(FORM_FIELDS, GROMMET_UI_FALLBACK_FIELD_TYPE);
// REGISTER GLOBAL WORKFLOW ACTION HANDLERS
WorkflowManager.registerGlobalActionHandlers(handlers);
// REGISTER ACTION COLUMN COMPONENTS
registerActionTypes(ACTION_TYPES);

// INIT HISTORY
const history = createHistory({
    basename: `${process.env.PUBLIC_URL}${BSS_URL_BASENAME_PREFIX}`,
    getUserConfirmation: (message, callback) => {
        const modal = document.createElement('div');
        document.body.appendChild(modal);

        const withCleanup = (answer) => {
            ReactDOM.unmountComponentAtNode(modal);
            document.body.removeChild(modal);
            callback(answer);
        };

        ReactDOM.render((
            <IntlProvider locale={locale} messages={messages[locale]}>
                <Grommet theme={theme}>
                    <LeavePageDialog
                        message={message}
                        onClose={withCleanup}
                        onProceed={withCleanup}
                    />
                </Grommet>
            </IntlProvider>
        ), modal);

        return message;
    },
});

let store = null;

// INIT API CLIENT
const apiClient = new ApiClient(
    process.env.REACT_APP_API_ROOT, resources, JWTBackend, {
        AuthStorageClass: AuthLocalStorage,
        authResourceName: 'auth',
    }, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Accept-Language': locale,
    }, (client) => {
        // Set the initial user
        if (client.authorized) {
            client.resources.auth.get().then((response) => {
                const dispatchUserActions = () => {
                    if (!store) {
                        setTimeout(dispatchUserActions, 100);
                    } else {
                        store.dispatch(setUser(response));
                        store.dispatch(setPermissions(response));
                    }
                };
                dispatchUserActions();
            }).catch((response) => {
                if (response.status === 401) {
                    client.authorized = false;
                    store.dispatch(removeUser());
                    store.dispatch(setPermissions({}));
                }
            });
        } else {
            store.dispatch(removeUser());
            store.dispatch(setPermissions({}));
        }
    },
);

// Add flags to the session storage which show if the page is loaded or not
const handleLoad = () => {
    sessionStorage.setItem('loaded', true);
};

const handleUnload = () => {
    sessionStorage.removeItem('loaded');
};

window.addEventListener('load', handleLoad);
window.addEventListener('unload', handleUnload);

// If the session storage already contains the loaded flag, it means that the
// tab is a duplicate of another tab. In this case, remove the wsConnectionId and the
// tabId so that they can be generated again for the tabs
// If the wsConnectionId is not removed, the duplicate tab will have the same connectionId as
// the tab from which it originated which might result in multiple landing tabs
if (sessionStorage.getItem('loaded')) {
    sessionStorage.removeItem('wsConnectionId');
    sessionStorage.removeItem('tabId');
}

// INIT WEBSOCKET CLIENT
const wsAuth = new ApiClientAuthBackend(apiClient);
const wsClient = new WebSocketClient(process.env.REACT_APP_WS_ENDPOINT, wsAuth, window.sessionStorage);

apiClient.setBaseHeaders({
    ...apiClient.getBaseHeaders(),
    'Ws-Connection-Id': wsClient.connectionId,
});

// INIT REDUX
let composeEnhancers = compose;
const middlewares = [sidePanelMiddleware(history), callingsMiddleware(wsClient), updateDocumentTitleMiddleware, thunk];

if (process.env.NODE_ENV !== 'production') {
    if (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) {
        composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
    }
    // middlewares.push(logger);
}

store = createStore(
    combineReducers({
        ...speedReducers,
        ...reducers,
    }),
    composeEnhancers(applyMiddleware(...middlewares)),
);


// INIT WORKFLOWS MANAGER AND HANDLERS
const workflowManager = new WorkflowManager({
    apiClient, wsClient, history, store,
});

ReactDOM.render((
    <Provider store={store}>
        <Router history={history}>
            <APIProvider apiClient={apiClient}>
                <WSContext.Provider value={wsClient}>
                    <WorkflowProvider workflowManager={workflowManager}>
                        <IntlProvider locale={locale} messages={messages[locale]}>
                            <App />
                        </IntlProvider>
                    </WorkflowProvider>
                </WSContext.Provider>
            </APIProvider>
        </Router>
    </Provider>
), document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
